import React, { useState, useEffect } from 'react';
import { SnackbarProvider } from 'notistack';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from './hooks/useAuthContext';
import { I18nextProvider } from 'react-i18next'; // Import I18nextProvider
import i18n from './i18n'; // Import i18n configuration
import { useJsApiLoader } from '@react-google-maps/api';

import Navbar from './components/Navbar'; 
import Signup from './components/Signup';
import Login from './components/Login';
import GoogleMissingEmail from './components/GoogleMissingEmail';
import Home from './pages/Home';
import LostPetsPage from './pages/pet-lost/LostPetsPage';
import LostPetDetailsPage from './pages/pet-lost/LostPetDetailsPage';
import ImagePreviewPage from './pages/pet-lost/ImagePreviewPage';
import PetList from './components/PetList';
import OrganizationPage from './pages/organization/OrganizationPage';
import OrganizationInvitationPage from './pages/organization/OrganizationInvitationPage'; 
import PetPage from './pages/pet/PetPage';
import WeightHistoryPage from './pages/pet/WeightHistoryPage';
import AddVaccinePage from './pages/pet/AddVaccinePage'; 
import VaccineDetailsPage from './pages/pet/VaccineDetailsPage';
import EditVaccinePage from './pages/pet/EditVaccinePage';
import AddDewormingPage from './pages/pet/AddDewormingPage'; 
import DewormingDetailsPage from './pages/pet/DewormingDetailsPage';
import EditDewormingPage from './pages/pet/EditDewormingPage';
import AddAllergyPage from './pages/pet/AddAllergyPage'; 
import AddDiseasePage from './pages/pet/AddDiseasePage';
import AddDisabilityPage from './pages/pet/AddDisabilityPage';
import ConditionPage from './pages/pet/ConditionPage';
import EditConditionPage from './pages/pet/EditConditionPage';
import EditTreatmentPage from './pages/pet/EditTreatmentPage';
import ManagePetImagesPage from './pages/pet/ManagePetImagesPage'; 
import FoundPetsPage from './pages/pet-found/FoundPetsPage';
import ReportFoundPet from './pages/pet-found/ReportFoundPet';
import FoundPetDetailsPage from './pages/pet-found/FoundPetDetailsPage'; 
import ManageFoundPetImagesPage from './pages/pet-found/ManageFoundPetImagesPage'; 
import EditFoundPetPage from './pages/pet-found/EditFoundPetPage';
import CreateFirstPet from './pages/pet/CreateFirstPet';
import CreatePet from './pages/pet/CreatePet'; 
import EditPetPage from './pages/pet/EditPetPage';
import AccountPage from './pages/account/AccountPage';
import EditAccountPage from './pages/account/EditAccountPage';
import SettingsPage from './pages/account/SettingsPage'; // Import SettingsPage
import EditSettingsPage from './pages/account/EditSettingsPage'; 
import ReportPetLost from './pages/pet-lost/ReportPetLost';
import ReportLostPetFound from './pages/pet-lost/ReportLostPetFound'; // Import ReportLostPetFound
import UpdateProfileImagePage from './pages/pet/UpdateProfileImagePage'; // Import UpdateProfileImagePage
import ManageLostPetImagesPage from './pages/pet/ManageLostPetImagesPage'; // Import ManageLostPetImagesPage

function GoogleCallback() {
    const { dispatch } = useAuthContext();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    useEffect(() => {
        if (token) {
            const user = { token }; // Store the token in the user object
            localStorage.setItem('user', JSON.stringify(user));
            dispatch({ type: 'LOGIN', payload: user });
            window.location.href = '/'; // Redirect to the home page
        }
    }, [token, dispatch]);

    return <div>Logging you in...</div>;
}

function App() {
    const { user, loading } = useAuthContext();
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: ['places', 'maps'],
    });

    useEffect(() => {
        // Fetch the user's language preference from localStorage or default to 'en'
        const storedUser = JSON.parse(localStorage.getItem('user'));
        const userLanguage = storedUser?.language || 'en';
        i18n.changeLanguage(userLanguage); // Dynamically set the language
        console.log('Language set to:', userLanguage); // Debugging: Log the language
    }, []);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!isLoaded) return <p>Loading Google Maps...</p>;
    if (loadError) return <p>Error loading Google Maps: {loadError.message}</p>;

    return (
        <I18nextProvider i18n={i18n}> 
            <SnackbarProvider maxSnack={3}> 
                <div className="App">
                    <BrowserRouter>
                        <Navbar />
                        <div>
                            <Routes>
                                <Route path="/" element={user ? <Home /> : <Navigate to="/login" />} />
                                <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
                                <Route path="/signup" element={!user ? <Signup /> : <Navigate to="/" />} />
                                <Route path="/lost-pets" element={<LostPetsPage isLoaded={isLoaded} />} />
                                <Route path="/lost-pet/:id" element={<LostPetDetailsPage isLoaded={isLoaded} />} />
                                <Route path="/image-preview" element={<ImagePreviewPage />} />
                                <Route path="/organizations/:orgId/pets" element={user ? <PetList /> : <Navigate to="/login" />} />
                                <Route path="/organizations/:orgId/invite" element={user ? <OrganizationInvitationPage /> : <Navigate to="/login" />} />
                                <Route path="/organizations/:orgId/members" element={user ? <OrganizationPage /> : <Navigate to="/login" />} /> 
                                <Route path="/create-pet" element={user ? <CreatePet /> : <Navigate to="/login" />} />
                                <Route path="/create-first-pet" element={user ? <CreateFirstPet /> : <Navigate to="/login" />} />
                                <Route path="/pet/:id" element={user ? <PetPage /> : <Navigate to="/login" />} />
                                <Route path="/edit-pet/:id" element={user ? <EditPetPage /> : <Navigate to="/login" />} />
                                <Route path="/pet/:id/report-lost" element={user ? <ReportPetLost /> : <Navigate to="/login" />} />
                                <Route path="/report-lost-pet-found/:id" element={<ReportLostPetFound />} /> {/* New route for ReportLostPetFound */}
                                <Route path="/pet/:id/update-profile-image" element={<UpdateProfileImagePage />} /> {/* New route for UpdateProfileImagePage */}
                                <Route path="/pet/:id/manage-images" element={<ManagePetImagesPage />} />
                                <Route path="/pet/:id/manage-lost-images" element={<ManageLostPetImagesPage />} />
                                <Route path="/pet/:id/weight-history" element={<WeightHistoryPage />} /> 
                                <Route path="/pet/:id/add-vaccine" element={<AddVaccinePage />} />
                                <Route path="/pet/:id/vaccine/:vaccineId" element={<VaccineDetailsPage />} />
                                <Route path="/pet/:id/vaccine/:vaccineId/edit" element={<EditVaccinePage />} />
                                <Route path="/pet/:id/add-deworming" element={<AddDewormingPage />} /> 
                                <Route path="/pet/:id/deworming/:dewormingId" element={<DewormingDetailsPage />} />
                                <Route path="/pet/:id/deworming/:dewormingId/edit" element={<EditDewormingPage />} />
                                <Route path="/pet/:id/add-allergy" element={<AddAllergyPage />} /> 
                                <Route path="/pet/:id/add-disease" element={<AddDiseasePage />} /> 
                                <Route path="/pet/:id/add-disability" element={<AddDisabilityPage />} />
                                <Route path="/pet/:id/conditions/:conditionId" element={<ConditionPage />} /> 
                                <Route path="/pet/:id/conditions/:conditionId/edit" element={<EditConditionPage />} />
                                <Route path="/pet/:id/conditions/:conditionId/treatments/:treatmentId/edit" element={<EditTreatmentPage />} />
                                <Route path="/found-pets" element={<FoundPetsPage />} />
                                <Route path="/report-found-pet" element={user ? <ReportFoundPet /> : <Navigate to="/login" />} />
                                <Route path="/found-pet/:id" element={<FoundPetDetailsPage />} />
                                <Route path="/found-pet/:id/edit" element={user ? <EditFoundPetPage /> : <Navigate to="/login" />} />
                                <Route path="/found-pet/:id/manage-images" element={user ? <ManageFoundPetImagesPage /> : <Navigate to="/login" />} />
                                <Route path="/account/:id" element={user ? <AccountPage /> : <Navigate to="/login" />} />
                                <Route path="/edit-account/:id" element={user ? <EditAccountPage /> : <Navigate to="/login" />} />
                                <Route path="/settings/:id" element={user ? <SettingsPage /> : <Navigate to="/login" />} /> 
                                <Route path="/edit-settings/:id" element={user ? <EditSettingsPage /> : <Navigate to="/login" />} /> 
                                <Route path="/google/callback" element={<GoogleCallback />} />
                                <Route path="/google-missing-email" element={<GoogleMissingEmail />} />
                            </Routes>
                        </div>
                    </BrowserRouter>
                </div>
            </SnackbarProvider>
        </I18nextProvider>
    );
}

export default App;